// Pixel-faithful recreation of the first/hero section from the old casasamba.com.
// Image pops in with a spring scale/fade. Layout, fonts and colors mirror the original.
import React from 'react';
import { motion } from 'framer-motion';

const HERO_IMG =
  'https://customer-assets-jt897jd0.emergentagent.net/job_casa-samba-music/artifacts/fc6614e742474a20_Bomani%202025%20brasil%20.png';

const btnDark = {
  fontFamily: 'Roboto, sans-serif',
  fontWeight: 900,
  fontSize: 18,
  lineHeight: '38px',
  color: '#fff',
  textDecoration: 'none',
  padding: '0 26px',
  borderRadius: 5,
  background: 'linear-gradient(#4a4a4a 0%, #2b2b2b 100%)',
  display: 'inline-block',
  whiteSpace: 'nowrap',
};

const btnBrand = {
  fontFamily: 'Roboto, sans-serif',
  fontWeight: 900,
  fontSize: 21,
  lineHeight: '38px',
  color: '#FFCC00',
  textDecoration: 'none',
  padding: '14px 30px',
  borderRadius: 25,
  background:
    'linear-gradient(95deg, #4a006b 0%, #4a006b 31%, #4a006b 44%, #53ad22 52%, #53ad22 100%)',
  display: 'inline-block',
  textAlign: 'center',
};

export default function LegacyHomeHero() {
  return (
    <>
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Manrope:wght@500;700;800&family=Roboto:wght@400;900&display=swap"
      />
      <section
        className="relative overflow-hidden bg-black"
        style={{ fontFamily: 'Manrope, sans-serif', minHeight: '1000px' }}
        data-testid="legacy-home-hero"
      >
        {/* Teal center strip (33, 156, 160) — the signature background band */}
        <div
          aria-hidden
          className="absolute inset-y-0"
          style={{ left: '15%', right: '15%', background: 'rgb(33, 156, 160)' }}
        />

        {/* Hero image of Bomani in the red costume — springs in with a pop, then breathes */}
        <motion.div
          className="absolute hidden md:block pointer-events-none select-none"
          style={{
            top: 60,
            left: '50%',
            marginLeft: 20,
            width: 'min(640px, 42vw)',
            maxHeight: 940,
            zIndex: 4,
          }}
          initial={{ opacity: 0, scale: 0.55, y: 60 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 180, damping: 14, mass: 1, delay: 0.15 }}
        >
          <motion.img
            loading="eager"
            decoding="async"
            src={HERO_IMG}
            alt="Casa Samba — Bomani in red costume"
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: 940,
              objectFit: 'contain',
              filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.6))',
            }}
            animate={{ y: [0, -8, 0, 6, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1.6 }}
          />
        </motion.div>

        {/* 1. Join pill — DEAD CENTER horizontally, small enough to clear Bomani's head */}
        <motion.div
          className="absolute"
          style={{
            top: 24,
            left: '50%',
            zIndex: 5,
          }}
          initial={{ opacity: 0, y: -20, x: '-50%' }}
          animate={{ opacity: 1, y: 0, x: '-50%' }}
          transition={{ duration: 0.6, delay: 0.25 }}
        >
          <a
            href="https://escoladesamba.casasamba.com/registration"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 900,
              fontSize: 14,
              lineHeight: '20px',
              color: '#FFCC00',
              textDecoration: 'none',
              padding: '10px 24px',
              borderRadius: 25,
              background:
                'linear-gradient(95deg, #4a006b 0%, #4a006b 31%, #4a006b 44%, #53ad22 52%, #53ad22 100%)',
              display: 'inline-block',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              letterSpacing: '0.5px',
            }}
            data-testid="legacy-hero-cta-join"
          >
            Join Casa Samba for Mardi Gras Brazil Carnaval Conference 2026
          </a>
        </motion.div>

        {/* 2. Fala Comigo Gente — on Bomani's right shoulder, next to ENTER (NOT on his face) */}
        <motion.div
          className="absolute pointer-events-none"
          style={{
            top: 230,
            left: '46%',
            zIndex: 5,
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 800,
            fontSize: 32,
            lineHeight: '38px',
            color: '#FFCC00',
            textShadow: '2px 2px 0 #000, -1px -1px 0 #000',
            letterSpacing: '0.3px',
            whiteSpace: 'nowrap',
            transformOrigin: '0% 100%',
          }}
          initial={{ opacity: 0, scale: 0, x: 40, y: 30, rotate: -12 }}
          animate={{ opacity: 1, scale: 1, x: 0, y: 0, rotate: -6 }}
          transition={{ type: 'spring', stiffness: 160, damping: 13, delay: 0.9 }}
          data-testid="legacy-hero-slogan"
        >
          Fala Comigo Gente
        </motion.div>

        {/* Centerpiece content — reordered stack */}
        <div
          className="relative mx-auto px-6 md:px-10"
          style={{ maxWidth: 1400, paddingTop: 220, paddingBottom: 60, zIndex: 3 }}
        >
          {/* 2. ENTER / CASA / SAMBA — one giant clickable brand mark that opens the live site */}
          <motion.a
            href="https://escoladesamba.casasamba.com/"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 1.1 }}
            className="block no-underline"
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 700,
              color: '#fff',
              textShadow: '4px 4px 0 #000',
              letterSpacing: '-5px',
              textDecoration: 'none',
              lineHeight: '130px',
              marginTop: 36,
              cursor: 'pointer',
            }}
            data-testid="legacy-hero-enter-link"
          >
            <div style={{ fontSize: 140 }}>ENTER</div>
            <div style={{ fontSize: 140 }}>CASA</div>
            <div style={{ fontSize: 144, lineHeight: '134px' }}>SAMBA</div>
          </motion.a>

          {/* 3. Small menu — Contact removed because the image covers that corner */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.5 }}
            style={{ marginTop: 36 }}
          >
            <nav className="flex flex-wrap gap-4" data-testid="legacy-hero-menu">
              <a href="https://escoladesamba.casasamba.com/classes" target="_blank" rel="noopener noreferrer" style={btnDark} data-testid="menu-dance">
                Brasilian Dance &amp; Drumming
              </a>
              <a href="https://escoladesamba.casasamba.com/quote" target="_blank" rel="noopener noreferrer" style={btnDark} data-testid="menu-quote">
                Get a Quote
              </a>
              <a href="https://escoladesamba.casasamba.com/performances" target="_blank" rel="noopener noreferrer" style={btnDark} data-testid="menu-performances">
                Types of Performances
              </a>
            </nav>
          </motion.div>
        </div>

        {/* Times-Picayune quote — left aligned, Bomani has clear space on the right */}
        <div
          className="relative mx-auto px-6 md:px-10"
          style={{ maxWidth: 1400, paddingBottom: 50, zIndex: 3 }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.75 }}
            style={{
              maxWidth: 580,
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 800,
              fontSize: 30,
              lineHeight: '36px',
              color: '#fff',
              background: 'rgba(15, 15, 15, 0.63)',
              padding: '50px 60px',
            }}
          >
            "Casa Samba The Next Best Thing To a Trip To Brasil". The Times Picayune
          </motion.div>
        </div>
      </section>
    </>
  );
}
